import { useCallback, useEffect, useMemo, useRef } from 'react';

import { User, useUserData } from '@givebacks/api/core';
import { useUpdateExtensionStatus } from '~/hooks/queries/Core/users';

const getInstallState = (userData?: User) => {
  return Boolean(userData?.onboarding?.installed_chrome);
};

/**
 * Get the `<mntoolbar>` element from the DOM.
 *
 * @returns The `<mntoolbar>` element, or `null` during SSR
 */
const getMntoolbarElement = () => {
  if (typeof document === 'undefined') return null;
  const element = document.querySelector<HTMLElement>('mntoolbar');
  if (!element) return null;
  return element;
};

/**
 * Manages updates from the Cartera Mn Toolbar extension.
 *
 * When mntoolbar is found in the dom tree, it will
 * ensure the api has been updated to reflect that the extension is installed.
 *
 */
const useMntoolbar = () => {
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const { mutate: updateExtensionStatus } = useUpdateExtensionStatus();
  const { data: userData } = useUserData();

  const hasInstalledChrome = useMemo(() => {
    if (!userData) return false;
    return getInstallState(userData);
  }, [userData]);

  const cleanUpMutationObserver = useCallback(() => {
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
      mutationObserverRef.current = null;
    }
  }, []);

  /**
   * Creates a mutation observer to detect when the `<mntoolbar>` element is added to the DOM.
   * If the element is found, it will update the extension status.
   * If the element is not found, it will create a new observer.
   * always cleans up the previous observer.
   */
  const createElementMutationObserver = useCallback(() => {
    cleanUpMutationObserver();
    const headElement = document.head;

    if (!headElement) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const foundMntoolbar = addedNodes.some(
            (node) => node.nodeName.toLowerCase() === 'mntoolbar'
          );
          if (foundMntoolbar) {
            updateExtensionStatus({ platform: 'Chrome', status: true });
            cleanUpMutationObserver();
            return;
          }
        }
      }
    });

    mutationObserverRef.current = observer;
    observer.observe(headElement, { childList: true, subtree: true });
  }, [cleanUpMutationObserver, updateExtensionStatus]);

  useEffect(() => {
    if (hasInstalledChrome) {
      return cleanUpMutationObserver();
    }
    const element = getMntoolbarElement();

    if (element) {
      updateExtensionStatus({ platform: 'Chrome', status: true });
      return;
    } else {
      createElementMutationObserver();
    }

    return cleanUpMutationObserver;
  }, [
    cleanUpMutationObserver,
    createElementMutationObserver,
    hasInstalledChrome,
    updateExtensionStatus,
  ]);

  return { hasInstalledChrome, mutationObserverRef };
};

export { useMntoolbar };
